import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Sparkles, MessagesSquare, BookOpen, Feather, Droplets, Star } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { BillingPortalButton } from "@/components/BillingPortalButton";

const perk = (Icon, text) => ({ Icon, text });

export default function Membership() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/membership/status").then((r) => setStatus(r.data));
  useEffect(() => { loadStatus(); }, [user]);

  const joinCommunity = async () => {
    if (!user) return navigate("/register");
    setBusy(true);
    try {
      const { data } = await api.post("/membership/join");
      if (data.status === "payment_required") {
        const res = await api.post("/membership/checkout", { origin_url: window.location.origin });
        if (res.data.resumed) {
          toast.success("Auto-renew resumed — welcome back ✦");
          loadStatus();
          return;
        }
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.success(data.status === "already_member" ? "You're already a Community Member ✦" : "Welcome to the Community ✦");
      loadStatus();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const cancelRenewal = async () => {
    try {
      const { data } = await api.post("/membership/cancel-renewal");
      toast.success(`Auto-renew cancelled — you're a member until ${data.active_until ? new Date(data.active_until).toLocaleDateString("en-CA") : "the end of your paid period"}`);
      loadStatus();
    } catch (e) { toast.error(apiError(e)); }
  };

  const tiers = [
    {
      name: "Ripple Member",
      price: "Free",
      sub: "forever",
      testid: "tier-free",
      perks: [
        perk(Check, "Browse & book vetted practitioners"),
        perk(Check, "Personal invite link, badges & My Ripples"),
        perk(Check, "Teasers of the Learn Library & Bridgett's Corner"),
        perk(Check, "Community newsletter"),
      ],
      cta: user ? "You're in ✦" : "Join free",
      action: () => (user ? null : navigate("/register")),
      disabled: !!user,
    },
    {
      name: "Community Member",
      price: status?.launch_offer_active ? "Free" : "$4.99",
      sub: status?.launch_offer_active ? "founding year — then $4.99/month" : "per month — renews automatically, cancel anytime",
      highlight: true,
      badge: status?.launch_offer_active ? "Launch special ✦" : null,
      testid: "tier-community",
      perks: [
        perk(Feather, "Full access to Bridgett's Corner"),
        perk(BookOpen, "Every Learn Library article, start to finish"),
        perk(MessagesSquare, "The Community Chat — one room, all of us"),
        perk(Droplets, "Everything in Ripple Member"),
      ],
      cta: status?.member && status?.community_member ? "You're a Community Member ✦" : busy ? "One moment…" : "Join the Community",
      action: joinCommunity,
      disabled: busy || (status?.member && status?.community_member),
    },
    {
      name: "Leader",
      price: "Free",
      sub: "your first year, then $15/month — for practitioners",
      testid: "tier-leader",
      perks: [
        perk(Sparkles, "Full practice profile — story, services, bookings"),
        perk(Check, "Listed free for your whole first year once approved"),
        perk(Check, "Keep 90% of every booking (10% community fee)"),
        perk(Star, "Optional Featured page — $25/month, only if you choose it"),
      ],
      cta: user?.role === "practitioner" ? "Open My Studio" : "List your practice",
      action: () => navigate(user?.role === "practitioner" ? "/dashboard" : "/register"),
    },
  ];

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="Membership — O.R.E.C" description="Three ways to belong: free Ripple membership, full Community access, or a Leader listing for practitioners." />
      <div className="max-w-5xl mx-auto" data-testid="membership-page">
        <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Membership</p>
        <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">Three ways to belong</h1>
        <p className="text-sm text-muted-foreground mb-12 max-w-xl leading-relaxed">
          Everyone starts a Ripple somewhere. Choose the way you want to be part of the community — you can always grow into more.
        </p>
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t) => (
            <div key={t.name} data-testid={t.testid}
              className={`silk-card rounded-lg p-7 flex flex-col ${t.highlight ? "border border-primary/50 relative" : ""}`}>
              {t.badge && (
                <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.18em] bg-primary text-primary-foreground rounded-full px-3 py-1">{t.badge}</span>
              )}
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t.name}</p>
              <p className="font-display text-4xl text-foreground">{t.price}</p>
              <p className="text-xs text-muted-foreground mb-6">{t.sub}</p>
              <ul className="space-y-3 mb-8 flex-1">
                {t.perks.map(({ Icon, text }, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Icon size={15} className="text-primary mt-0.5 shrink-0" /> {text}
                  </li>
                ))}
              </ul>
              <Button data-testid={`${t.testid}-cta`} disabled={t.disabled} onClick={t.action}
                variant={t.highlight ? "default" : "outline"} className="rounded-full w-full">
                {t.cta}
              </Button>
            </div>
          ))}
        </div>
        {status?.community_auto_renew && (
          <p className="text-xs text-muted-foreground mt-8 text-center" data-testid="membership-autorenew-row">
            Your Community membership renews automatically each month
            {status?.community_paid_until ? ` — paid through ${new Date(status.community_paid_until).toLocaleDateString("en-CA")}` : ""}.{" "}
            <button data-testid="membership-cancel-renewal-btn" onClick={cancelRenewal} className="underline hover:text-foreground">Cancel auto-renew</button>
          </p>
        )}
        {status?.community_paid_until && (
          <div className="mt-4 flex flex-col items-center gap-2" data-testid="membership-billing-row">
            <BillingPortalButton testid="membership-billing-portal-btn" />
            <p className="text-[11px] text-muted-foreground">Update your saved card or download past invoices — securely through Stripe.</p>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-10 text-center">
          Community Membership is free for everyone who joins during our founding launch year — after that it's just $4.99/month, renewing automatically so you never lapse (cancel anytime). Leaders control their listing and Featured months, on or off anytime.
        </p>
      </div>
    </div>
  );
}
